<template>
  <div class="embed-profile" :class="{ 'is-embedded': is_embedded, 'in-platform': in_platform }">
    <template v-if="loaded_service">

      <!-- Error al obtener el perfil -->
      <div v-if="error_data" class="p-1">
        <div class="alert-card">
          <div class="alert-icon">
            <feather-icon icon="LockIcon" size="20" />
          </div>
          <div class="alert-content">
            <span class="text-gray f-16">{{ $t('creator.unableProfile') }}</span>
            <p class="text-gray avenir-medium mb-0">{{ $t('creator.againLater') }}</p>
          </div>
        </div>
      </div>

      <div v-else class="profile-layout">
        <!-- Mini card con avatar y datos generales -->
        <aside class="profile-card">
          <div class="profile-card__avatar-wrap">
            <img :src="getAvatar()" class="profile-card__avatar" alt="avatar" @error="$event.target.src = background_default" />
            <span class="profile-card__network" :style="{ backgroundColor: network_color }">
              <b-img :src="network_icon" class="profile-card__network-icon" />
            </span>
          </div>
          <div class="profile-card__info">
            <h4 class="profile-card__name">{{ display_name }}</h4>
            <span class="profile-card__username">@{{ username }}</span>
            <span class="profile-card__followers">{{ getFormat(followers_count) }} {{ $t('campaigns.followers').toLowerCase() }}</span>
            <span v-if="country" class="profile-card__country">
              <feather-icon icon="MapPinIcon" size="13" class="mr-25" />{{ country }}
            </span>
          </div>
          <div class="profile-card__actions">
            <hire-creator-button v-if="proposal_vanity_name" @click="openProposals()" />
            <b-button class="card-action card-action--outline" variant="none" @click="openFullReport()">
              <feather-icon icon="BarChart2Icon" size="16" />
              <span>{{ $t('search_preview.seeFullReport') }}</span>
            </b-button>
          </div>
        </aside>

        <!-- Resumen del informe -->
        <div class="profile-content">
          <content-profile-analytics
            v-if="network === 'instagram' || network === 'youtube'"
            :data_user="data_user"
            :network="network"
            :is_paid="is_paid"
            :has_expired="has_expired"
            :subscribers_growth_prc_graph="subscribers_growth_prc_graph"
            :mentions_avatares="mentions_avatares"
            :mentions_user="mentions_user"
            :categories="formatted_categories"
            @get_profile_url="openFullReport"
          />

          <content-profile-analytics-twitter
            v-else-if="network === 'twitter'"
            :data_user="data_user"
            :is_paid="is_paid"
            :network="network"
            @get_profile_url="openFullReport"
          />

          <content-profile-analytics-twitch
            v-else-if="network === 'twitch'"
            :is_paid="is_paid"
            :data_user="data_user"
            :network="network"
            @get_profile_url="openFullReport"
          />

          <content-profile-analytics-tiktok
            v-else-if="network === 'tiktok'"
            :data_user="data_user"
            :network="network"
            :is_paid="is_paid"
            :has_expired="has_expired"
            :subscribers_growth_prc_graph="subscribers_growth_prc_graph"
            :mentions_avatares="mentions_avatares"
            :mentions_user="mentions_user"
            :categories="formatted_categories"
            @get_profile_url="openFullReport"
          />
        </div>
      </div>
    </template>

    <!-- Skeleton mientras carga -->
    <div v-else class="profile-layout">
      <aside class="profile-card">
        <b-skeleton type="avatar" class="profile-card__avatar mb-1" />
        <b-skeleton width="70%" />
        <b-skeleton width="50%" />
        <b-skeleton type="button" class="w-100 mt-1" />
      </aside>
      <div class="profile-content">
        <div class="mb-2 min-height-281">
          <b-skeleton class="w-100 h-100"></b-skeleton>
        </div>
        <div class="mb-2 min-height-321">
          <b-skeleton class="w-100 h-100"></b-skeleton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BImg, BButton, BSkeleton } from 'bootstrap-vue';
import {
  getSummaryInfluencer,
  getHistorySuscribers,
  getHistorySuscribersLaravel,
  getCategories,
} from '@/services/search-refactored';
import { hasProposal } from '@/services/proposals-refactored';
import { getFormat } from '@/libs/utils/formats';
import { getIconWhite, getColor } from '@/libs/utils/icons';

export default {
  name: 'EmbedProfile',
  components: {
    BImg,
    BButton,
    BSkeleton,
    HireCreatorButton: () => import('@/views/components/proposals/HireCreatorButton.vue'),
    ContentProfileAnalytics: () => import('@/views/components/sidebar/ContentProfileAnalytics.vue'),
    ContentProfileAnalyticsTwitch: () => import('@/views/components/sidebar/twitch/ContentProfileAnalyticsTwitch.vue'),
    ContentProfileAnalyticsTwitter: () => import('@/views/components/sidebar/twitter/ContentProfileAnalyticsTwitter.vue'),
    ContentProfileAnalyticsTiktok: () => import('@/views/components/sidebar/tiktok/ContentProfileAnalyticsTiktok.vue'),
  },
  props: {
    // Se usa dentro de la plataforma (perfil sin sesión) además de la ruta pública para iframes
    in_platform: {
      type: Boolean,
      default: false,
    },
    network_dad: {
      type: String,
      default: null,
    },
    username_dad: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      getFormat,
      background_default: require('@/assets/images/dashboard/imgDefault.png'),
      data_user: null,
      error_data: false,
      loaded_service: false,
      is_paid: false,
      has_expired: false,
      subscribers_growth_prc_graph: null,
      mentions_user: null,
      mentions_avatares: [],
      categories: [],
      proposal_vanity_name: null,
      is_embedded: window.parent !== window,
      resize_observer: null,
      last_height_sent: 0,
    };
  },
  computed: {
    network() {
      return this.network_dad || this.$route.params.network;
    },
    username() {
      return this.username_dad || this.$route.params.username;
    },
    display_name() {
      const basic = this.data_user?.basic;
      return basic?.title || basic?.username || this.username;
    },
    followers_count() {
      return this.data_user?.metrics?.subscribers_count?.value || 0;
    },
    network_icon() {
      return getIconWhite(this.network);
    },
    network_color() {
      return getColor(this.network, true);
    },
    country() {
      return this.data_user?.features?.blogger_geo?.data?.country || '';
    },
    formatted_categories() {
      if (!this.categories.length || !this.data_user?.features) return [];
      const IDs = this.data_user.features.blogger_labels?.data?.slice(0, 6) || [];
      return IDs.map(id => this.categories.find(cat => cat.id === id)?.title).filter(Boolean);
    },
  },
  created() {
    if (!this.in_platform) document.body.style.backgroundColor = 'white';
    this.getData();
  },
  mounted() {
    // El contenido cambia de alto al cargar imágenes y gráficas, el iframe de WordPress se ajusta con este aviso
    if (window.ResizeObserver) {
      this.resize_observer = new ResizeObserver(this.sendHeightToParent);
      this.resize_observer.observe(this.$el);
    }
  },
  beforeDestroy() {
    if (this.resize_observer) this.resize_observer.disconnect();
  },
  destroyed() {
    if (!this.in_platform) document.body.removeAttribute('style');
  },
  methods: {
    getData() {
      this.getCategoriesService();
      this.getProposal();

      getSummaryInfluencer(this.network, this.username)
        .then((response) => {
          if (!response || response.error || !response.report) {
            this.error_data = true;
            return;
          }
          this.data_user = response.report;
          this.is_paid = response.is_paid;
          this.has_expired = response.is_expired;
          this.setMentions();
        })
        .catch(() => {
          this.error_data = true;
        })
        .finally(() => {
          this.loaded_service = true;
        });

      if (['instagram', 'youtube', 'tiktok'].includes(this.network)) this.fetchSubscribersHistory();
    },
    setMentions() {
      const blogger_mentions = this.data_user?.features?.blogger_mentions;
      const mentions_data = Array.isArray(blogger_mentions) ? null : blogger_mentions?.data;
      const performance = mentions_data?.mentions?.performance?.['180d'];
      this.mentions_user = null;
      this.mentions_avatares = [];

      if (this.network === 'instagram' && performance) {
        this.mentions_user = performance;
        this.mentions_avatares = Object.values(performance.users || {})
          .map(user => user.avatar_url)
          .slice(0, (mentions_data.reports || []).length);
      } else if ((this.network === 'tiktok' || this.network === 'youtube') && Array.isArray(performance?.data) && performance.data.length > 0) {
        this.mentions_user = performance;
        this.mentions_avatares = Object.values(mentions_data.users || {})
          .map(user => user?.avatar_url)
          .filter(Boolean)
          .slice(0, performance.data.length);
      }
    },
    getProposal() {
      // El botón de contratar solo se muestra si el creador tiene propuestas publicadas
      hasProposal(this.network, this.username)
        .then((response) => {
          this.proposal_vanity_name = response?.has_proposals && response?.vanity_name ? response.vanity_name : null;
        })
        .catch(() => {
          this.proposal_vanity_name = null;
        });
    },
    getCategoriesService() {
      // Taxonomía del core; sin sesión puede fallar y solo se omiten las categorías
      getCategories(this.network)
        .then((response) => {
          this.categories = response?.labels ?? [];
        })
        .catch(() => {
          this.categories = [];
        });
    },
    fetchSubscribersHistory() {
      const fetchFunction = this.network === 'instagram' ? getHistorySuscribers : getHistorySuscribersLaravel;
      const metric_key = this.network === 'instagram' ? 'followers_count' : 'subscribers_count';

      fetchFunction({ network: this.network, username: this.username })
        .then((response) => {
          const history = response?.metrics?.[metric_key]?.history;
          if (Array.isArray(history)) {
            this.subscribers_growth_prc_graph = history.map(item => ({
              count: item.value,
              date: Math.floor(new Date(item.time_iso).getTime() / 1000),
            }));
          }
        })
        .catch(() => {});
    },
    getAvatar() {
      const basic = this.data_user?.basic || {};
      if (basic.avatar_url) return basic.avatar_url;
      const username_obj = basic.username || {};
      if (username_obj.name?.ava || username_obj.name?.avatar) return username_obj.name.ava || username_obj.name.avatar;
      if (username_obj.avatar) return username_obj.avatar;
      return this.background_default;
    },
    openFullReport() {
      // Dentro de la plataforma se inicia sesión y el login regresa a este mismo perfil
      if (this.in_platform) {
        this.$router.push({ name: 'auth-login', query: { redirect: this.$route.fullPath } });
        return;
      }
      const url = this.$router.resolve({ name: 'auth-perfil', params: { network: this.network, alias: this.username } }).href;
      window.open(`${window.location.origin}${url}`, '_blank');
    },
    openProposals() {
      const route = { name: 'view_proposals', params: { vanity_name: this.proposal_vanity_name } };
      if (this.in_platform) {
        this.$router.push(route);
        return;
      }
      const url = this.$router.resolve(route).href;
      window.open(`${window.location.origin}${url}`, '_blank');
    },
    sendHeightToParent() {
      if (!this.is_embedded) return;
      const height = Math.ceil(this.$el.scrollHeight);
      if (height === this.last_height_sent) return;
      this.last_height_sent = height;
      window.parent.postMessage({ type: 'brandme-iframe-height', height }, '*');
    },
  },
};
</script>

<style scoped lang="scss">
.embed-profile {
  background-color: white;
  min-height: 100vh;
}
.is-embedded,
.in-platform {
  min-height: auto;
}
.in-platform {
  background-color: transparent;

  .profile-layout {
    padding: 0;
  }
  // Queda debajo de la barra fija de la plataforma; desde 1200px también hay menú horizontal
  .profile-card {
    top: 86px;

    @media (min-width: 1200px) {
      top: 165px;
    }
  }
}
.profile-layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
  padding: 1rem;
  background-color: #F5F8FA;

  // En tablet la card pasa arriba para que el informe tenga todo el ancho
  @media (max-width: 991px) {
    grid-template-columns: minmax(0, 1fr);
  }
  @media (max-width: 767px) {
    padding: 0.5rem;
  }
}
.profile-card {
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem 1.25rem;
  background-color: white;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  box-shadow: 0 1px 4px #0b14260a;

  @media (max-width: 991px) {
    position: static;
  }

  // Tablet: card horizontal con avatar, datos y botones en una fila
  @media (min-width: 576px) and (max-width: 991px) {
    flex-direction: row;
    text-align: left;
    gap: 1.5rem;

    .profile-card__avatar-wrap {
      margin-bottom: 0;
    }
    .profile-card__info {
      flex: 1;
      min-width: 0;
      align-items: flex-start;
    }
    .profile-card__actions {
      width: 260px;
      flex-shrink: 0;
      margin-top: 0;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__avatar-wrap {
    position: relative;
    margin-bottom: 1rem;
  }
  &__avatar {
    width: 120px;
    height: 120px;
    object-fit: cover;
    border-radius: 50%;
    background-color: #e0e0e0;
  }
  &__network {
    position: absolute;
    right: 2px;
    bottom: 2px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 3px solid white;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__network-icon {
    width: 15px;
    height: 15px;
  }
  &__name {
    font-family: 'avenir-bold';
    margin-bottom: 0.2rem;
    word-break: break-word;
  }
  &__username {
    font-size: 13px;
    color: #718096;
  }
  &__followers {
    font-size: 14px;
    font-family: 'avenir-medium';
    color: #4a5568;
    margin-top: 0.3rem;
  }
  &__country {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #718096;
    margin-top: 0.3rem;
  }
}
.profile-card__actions {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  width: 100%;
  margin-top: 1.1rem;
}
.card-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 46px;
  padding: 0.6rem 1rem;
  border-radius: 10px;
  box-shadow: none;
  transition: background-color 0.15s, border-color 0.15s;

  &--outline {
    border: 1px solid #dcd8f3;
    background: white;
    color: #1f2937;

    &:hover,
    &:focus {
      color: #1f2937;
      border-color: #7367f0;
      background: #f8f7ff;
    }
  }
}
// Los botones "Ver informe avanzado" del informe toman el estilo del de la card.
// Solo en esta vista: los mismos componentes se usan en el sidebar del analizador del dashboard
.profile-content ::v-deep .blue-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 46px;
  border: 1px solid #dcd8f3 !important;
  border-radius: 10px;
  background-color: white !important;
  color: #1f2937 !important;
  box-shadow: none !important;

  &::before {
    content: '';
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    background-color: currentColor;
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M18 20V10M12 20V4M6 20v-6'/%3E%3C/svg%3E") no-repeat center / contain;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M18 20V10M12 20V4M6 20v-6'/%3E%3C/svg%3E") no-repeat center / contain;
  }

  // La moneda de "Desbloquear" es clara (pensada para el fondo azul); sobre blanco se oscurece
  .icon-coin {
    filter: brightness(0);
    opacity: 0.85;
  }

  &:hover,
  &:focus {
    border-color: #7367f0 !important;
    background-color: #f8f7ff !important;
    color: #1f2937 !important;
    box-shadow: none !important;
  }
}
.mb-02 {
  margin-bottom: 0.4em;
}
.min-height-281 {
  height: 281px;
}
.min-height-321 {
  height: 321px;
}
.alert-card {
  display: flex;
  background-color: #b8c2cc;
  border-radius: 6px;
  margin-top: 1rem;
}
.alert-icon {
  align-items: center;
  background-color: #f87171;
  color: white;
  padding: 1.5rem;
  border-radius: 4px 0px 0px 4px;
  display: flex;
  justify-content: center;
}
.alert-content {
  padding: 1rem;
  display: flex;
  flex-direction: column;
}
.f-16 {
  font-size: 16px;
}
</style>
